import { BillingHistory } from '@app/types/payment/billing';

/** An a la carte purchase as /api/alacarte/purchases/ returns it. */
export interface PurchaseRecord {
  id?: number;
  product_name?: string;
  product?: { name?: string };
  quantity?: number;
  unit_price?: string | number;
  total_price?: string | number;
  purchase_date: string;
  payment_method?: string;
}

/**
 * Billing history rows: the Stripe invoices for the subscription, plus one
 * row per a la carte purchase, newest first.
 *
 * A purchase is charged without a Stripe invoice, so it has no invoice
 * number. It used to be given one here, continuing the Stripe sequence, which
 * showed numbers Stripe never issued (ZLAYT-1476).
 */
export function buildBillingRows(
  invoices: BillingHistory[],
  purchases: PurchaseRecord[]
): BillingHistory[] {
  const oneTime: BillingHistory[] = purchases.map((p) => ({
    date: new Date(p.purchase_date).getTime(),
    description: `${p.quantity || 1} × ${p.product_name || p.product?.name || 'A la carte'} (One-time)`,
    amount: Number(
      p.total_price ??
        (p.unit_price ? Number(p.unit_price) * Number(p.quantity || 1) : 0)
    ),
    status: 'paid',
    invoice_url: '',
    period: '',
    invoice_number: '',
    payment_method: p.payment_method || '',
    purchase_id: p.id,
  }));
  return [...invoices, ...oneTime].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );
}

export type BillingDocument =
  | { kind: 'invoice'; url: string }
  | { kind: 'receipt'; purchaseId: number };

/**
 * The Stripe document behind a row: the invoice Stripe issued for a
 * subscription charge, or the receipt for a one-time purchase.
 */
export function billingDocumentFor(
  row: BillingHistory
): BillingDocument | null {
  if (row.invoice_url) return { kind: 'invoice', url: row.invoice_url };
  if (row.purchase_id != null) {
    return { kind: 'receipt', purchaseId: row.purchase_id };
  }
  return null;
}
